// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import userDataService from '../services/userDataService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Inicio de sesión con el token personalizado de Firebase
  const login = async (customToken) => {
    try {
      console.log('[AuthContext] Iniciando login con customToken', customToken ? 'presente' : 'ausente');
      setError(null);
      setLoading(true);
      
      if (!customToken) {
        throw new Error('No se proporcionó un token de autenticación');
      }

      // Forzar persistencia local antes del login
      const { setPersistence, browserLocalPersistence } = require('firebase/auth');
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('[AuthContext] Persistencia local configurada correctamente');
      } catch (persistenceError) {
        console.error('[AuthContext] Error configurando persistencia:', persistenceError);
        // Continuamos con el flujo aunque falle persistencia
      }

      // Guardar información adicional para diagnóstico
      sessionStorage.setItem('authAttemptTime', new Date().toISOString());
      
      // Clear any previous auth errors
      sessionStorage.removeItem('authError');
      
      console.log('[AuthContext] Llamando a signInWithCustomToken');
      const userCredential = await signInWithCustomToken(auth, customToken);

      if (!userCredential?.user) {
        const msg = 'No se pudo autenticar al usuario: credenciales inválidas';
        console.error('[AuthContext] ' + msg);
        sessionStorage.setItem('authError', msg);
        throw new Error(msg);
      }
      
      // Guardamos explícitamente el token para forzar persistencia
      if (userCredential.user) {
        const currentToken = await userCredential.user.getIdToken(true);
        console.log('[AuthContext] Token actualizado y almacenado en memoria');
        // Persistimos el token personalizado de Tiendanube para futuros usos en ApiTokenService
        localStorage.setItem('tn_custom_token', customToken);
        localStorage.setItem('firebase_token_timestamp', Date.now());
      }
      
      console.log('[AuthContext] Usuario autenticado correctamente:', userCredential.user.uid);
      sessionStorage.setItem('authSuccess', 'true');
      sessionStorage.setItem('authSuccessTime', new Date().toISOString());

      // IMPORTANT: Establecer currentUser de inmediato para que los componentes
      // autenticados (PrivateRoute) detecten el login sin esperar el observer.
      setCurrentUser(userCredential.user);

      // Obtener datos adicionales del usuario
      console.log('[AuthContext] Obteniendo datos del usuario desde Firestore');
      const userData = await fetchUserData(userCredential.user.uid);
      
      if (!userData) {
        console.warn('[AuthContext] No se encontraron datos del usuario en Firestore');
      } else {
        console.log('[AuthContext] Datos del usuario obtenidos correctamente');
      }
      
      return userCredential.user;
    } catch (err) {
      console.error('[AuthContext] Error al iniciar sesión:', err);
      const errorMessage = err.response?.data?.message || 
                         err.message || 
                         'Error al iniciar sesión. Por favor, intente nuevamente.';
      
      // Guardar el error para diagnóstico
      sessionStorage.setItem('authError', errorMessage);
      sessionStorage.setItem('authErrorTime', new Date().toISOString());
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Cerrar sesión
  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setCurrentUser(null);
      setUserData(null);
      
      // Limpiar datos en UserDataService
      userDataService.initialize(null);
    } catch (err) {
      console.error('Error al cerrar sesión:', err);
      setError('Error al cerrar sesión. Por favor, intente nuevamente.');
      throw err;
    }
  };

  // Obtener datos del usuario desde Firestore
  const fetchUserData = async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        const formattedUserData = {
          ...data,
          tiendaSubdominio: uid,
          // Convertir Timestamps a Date para facilitar uso en componentes
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          tokenExpiresAt: data.tokenExpiresAt?.toDate()
        };
        
        // Actualizar el estado local
        setUserData(formattedUserData);
        
        // Inicializar el UserDataService con los datos del usuario
        userDataService.initialize(formattedUserData);
        
        return data;
      } else {
        console.warn(`Usuario ${uid} no encontrado en Firestore`);
        setUserData(null);
        
        // Limpiar datos en UserDataService
        userDataService.initialize(null);
        return null;
      }
    } catch (err) {
      console.error('Error al obtener datos de usuario:', err);
      setError('Error al cargar los datos del usuario.');
      setUserData(null);
      throw err;
    }
  };

  // Observer para cambios en el estado de autenticación
  useEffect(() => {
    console.log('[AuthContext] Configurando observer onAuthStateChanged');
    
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log('[AuthContext] Cambio en estado de autenticación detectado:', user ? `Usuario: ${user.uid}` : 'No autenticado');
      
      if (user) {
        // User is signed in
        sessionStorage.setItem('authStateChangeTime', new Date().toISOString());
        sessionStorage.setItem('authState', 'signed_in');
        sessionStorage.setItem('authUserId', user.uid);
        
        // Actualizar el estado de currentUser inmediatamente
        setCurrentUser(user);
        
        console.log('[AuthContext] Obteniendo datos complementarios del usuario');
        try {
          await fetchUserData(user.uid);
        } catch (err) {
          console.error('[AuthContext] Error al cargar datos del usuario:', err);
          // Fallback: establecer datos mínimos para permitir que la app continúe
          const fallbackData = {
            tienda: user.uid,
            uid: user.uid,
            email: user.email || null,
            fallback: true,
          };
          setUserData(fallbackData);
          userDataService.initialize(fallbackData);
        }
      } else {
        // User is signed out
        console.log('[AuthContext] Usuario no autenticado');
        sessionStorage.setItem('authState', 'signed_out'); 
        sessionStorage.setItem('authStateChangeTime', new Date().toISOString());
        
        setCurrentUser(null);
        setUserData(null);
      }
      
      setLoading(false);
    }, (error) => {
      console.error('[AuthContext] Error en observer de autenticación:', error);
      sessionStorage.setItem('authObserverError', error.message || error.toString());
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    userDataLoaded: !!userData,
    authenticated: !!currentUser,
    loading,
    error,
    login,
    logout,
    fetchUserData,
  };

  // Diagnóstico del estado actual para debugging
  useEffect(() => {
    if (!loading) {
      console.log('[AuthContext] Estado actual:', {
        authenticated: !!currentUser,
        userDataLoaded: !!userData,
        error: error
      });
    }
  }, [loading, currentUser, userData, error]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
